/**
 * IM (인스턴트 메시지) 스토어
 *
 * 카카오톡, 라인 등 메신저 메시지 관리
 * - 스레드 목록: Firestore에서 조회하여 메모리에 저장
 * - 메시지: 스레드 선택 시 서브컬렉션에서 조회 (캐시 안 함)
 * - 동기화: 수동 호출 (버전 감지 자동 동기화 없음)
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore'
import { getFirebaseDb } from '@/services/firebaseService'
import type {
  ImThreadDocument,
  ImMessage,
  ImDailyMessagesDocument,
} from '@farmfree/common/types/message/imMessage'
import type { Timestamp } from '@farmfree/common/firebase/firebaseTypes'

/**
 * Timestamp를 밀리초 숫자로 변환
 */
const toMilliseconds = (time: Timestamp | undefined): number => {
  if (!time) return 0
  return time.toMillis()
}

export const useImStore = defineStore('im', () => {
  // === 상태 ===
  const threads = ref<ImThreadDocument[]>([])
  const currentThread = ref<ImThreadDocument | null>(null)
  const currentMessages = ref<ImMessage[]>([]) // 선택된 스레드의 메시지
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // === Computed ===

  /** 마지막 메시지 시간순 정렬된 스레드 목록 */
  const sortedThreads = computed(() => {
    return [...threads.value].sort((a, b) => {
      const timeA = toMilliseconds(a.lastMessageTime)
      const timeB = toMilliseconds(b.lastMessageTime)
      return timeB - timeA
    })
  })

  // === Actions ===

  /**
   * 스레드 목록 조회 (수동 호출)
   * @param adminUid 관리자 UID
   */
  async function fetchThreads(adminUid: string): Promise<void> {
    if (!adminUid) {
      error.value = 'adminUid가 필요합니다'
      return
    }

    isLoading.value = true
    error.value = null

    try {
      const db = getFirebaseDb()
      const threadsRef = collection(db, 'v2_administrators', adminUid, 'im_threads')
      const q = query(threadsRef, orderBy('lastMessageTime', 'desc'))
      const snapshot = await getDocs(q)

      threads.value = snapshot.docs.map((doc) => ({
        ...doc.data(),
        thread_id: doc.id,
      })) as ImThreadDocument[]

      console.log(`✅ IM 스레드 ${threads.value.length}개 조회 완료`)
    } catch (err) {
      console.error('❌ IM 스레드 조회 실패:', err)
      error.value = err instanceof Error ? err.message : 'IM 스레드 조회 실패'
      throw err // 호출자가 에러를 처리할 수 있도록 throw
    } finally {
      isLoading.value = false
    }
  }

  /**
   * 스레드 선택 + 메시지 조회
   * @param threadId 스레드 ID
   * @param adminUid 관리자 UID
   */
  async function selectThread(threadId: string, adminUid: string): Promise<void> {
    // 스레드 찾기
    const thread = threads.value.find((t) => t.thread_id === threadId)
    if (!thread) {
      error.value = '스레드를 찾을 수 없습니다'
      return
    }

    currentThread.value = thread
    isLoading.value = true
    error.value = null

    try {
      // 최근 7일치 메시지 조회
      const db = getFirebaseDb()
      const messagesRef = collection(
        db,
        'v2_administrators',
        adminUid,
        'im_threads',
        threadId,
        'messages'
      )
      const q = query(messagesRef, orderBy('date', 'desc'), limit(7))
      const snapshot = await getDocs(q)

      // 날짜별 문서에서 메시지 추출
      const allMessages: ImMessage[] = []
      snapshot.docs.forEach((doc) => {
        const data = doc.data() as ImDailyMessagesDocument
        if (data.messages && Array.isArray(data.messages)) {
          allMessages.push(...data.messages)
        }
      })

      // 시간순 정렬 (오래된 것부터)
      currentMessages.value = allMessages.sort((a, b) => {
        const timeA = toMilliseconds(a.dateMessageCreated)
        const timeB = toMilliseconds(b.dateMessageCreated)
        return timeA - timeB
      })

      console.log(`✅ IM 메시지 ${currentMessages.value.length}개 조회 완료`)
    } catch (err) {
      console.error('❌ IM 메시지 조회 실패:', err)
      error.value = err instanceof Error ? err.message : 'IM 메시지 조회 실패'
      currentMessages.value = []
      throw err // 호출자가 에러를 처리할 수 있도록 throw
    } finally {
      isLoading.value = false
    }
  }

  /**
   * 특정 날짜의 메시지 조회
   * @param threadId 스레드 ID
   * @param date 날짜 (YYYY-MM-DD)
   * @param adminUid 관리자 UID
   */
  async function fetchMessagesForDate(
    threadId: string,
    date: string,
    adminUid: string
  ): Promise<ImMessage[]> {
    try {
      const db = getFirebaseDb()
      const messagesRef = collection(
        db,
        'v2_administrators',
        adminUid,
        'im_threads',
        threadId,
        'messages'
      )
      const snapshot = await getDocs(query(messagesRef))

      const dateDoc = snapshot.docs.find((doc) => doc.id === date)
      if (!dateDoc) {
        return []
      }

      const data = dateDoc.data() as ImDailyMessagesDocument
      return data.messages || []
    } catch (err) {
      console.error('❌ IM 날짜별 메시지 조회 실패:', err)
      return []
    }
  }

  /**
   * 스레드 선택 해제
   */
  function clearCurrentThread(): void {
    currentThread.value = null
    currentMessages.value = []
  }

  /**
   * 스토어 초기화 (로그아웃 시)
   */
  function $reset(): void {
    threads.value = []
    currentThread.value = null
    currentMessages.value = []
    isLoading.value = false
    error.value = null
  }

  return {
    // 상태
    threads,
    currentThread,
    currentMessages,
    isLoading,
    error,

    // Computed
    sortedThreads,

    // Actions
    fetchThreads,
    selectThread,
    fetchMessagesForDate,
    clearCurrentThread,
    $reset,
  }
})
